import React from 'react';
import { motion } from 'framer-motion';

/** Soft pill section label — used at the top of every section */
export const SectionLabel = ({ children, ...rest }) => (
    <span
        className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#F2F0FE] text-[#7B61FF] text-[12px] font-semibold tracking-wide"
        {...rest}
    >
        <span className="w-1.5 h-1.5 rounded-full bg-[#7B61FF]" />
        {children}
    </span>
);

export const PageHero = ({ label, title, subtitle, testId, bg }) => (
    <section className="relative pt-36 pb-20 overflow-hidden" data-testid={testId || 'page-hero'}>
        {/* Optional 3D background (per-page personality) */}
        {bg && <div className="absolute inset-0 z-0">{bg}</div>}
        <div className="absolute inset-0 hero-aura pointer-events-none z-[1]" />
        <div className="absolute inset-0 grid-pattern-light opacity-40 [mask-image:radial-gradient(ellipse_at_top,black,transparent_75%)] z-[1]" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7 }}
                className="max-w-4xl"
            >
                {label && (
                    <div className="mb-6">
                        <SectionLabel>{label}</SectionLabel>
                    </div>
                )}
                <h1 className="font-display text-4xl sm:text-5xl md:text-6xl font-semibold tracking-tight leading-[1.1] text-[#1A1A2E] text-balance heading-glow">
                    {title}
                </h1>
                {subtitle && (
                    <p className="mt-6 text-[17px] sm:text-lg text-[#4A4A5A] max-w-2xl leading-relaxed">
                        {subtitle}
                    </p>
                )}
            </motion.div>
        </div>
    </section>
);

export const FadeIn = ({ children, delay = 0, className = '', y = 24 }) => (
    <motion.div
        initial={{ opacity: 0, y }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-50px' }}
        transition={{ duration: 0.6, delay, ease: 'easeOut' }}
        className={className}
    >
        {children}
    </motion.div>
);

export const PrimaryButton = ({ children, className = '', ...rest }) => (
    <button
        className={`inline-flex items-center gap-2 bg-[#1A1A2E] text-white font-semibold text-[14px] px-6 py-3 rounded-full hover:bg-[#7B61FF] transition-all duration-300 ${className}`}
        {...rest}
    >
        {children}
    </button>
);

export const GhostButton = ({ children, className = '', ...rest }) => (
    <button
        className={`inline-flex items-center gap-2 bg-white text-[#1A1A2E] border border-[#E8E8EC] font-semibold text-[14px] px-6 py-3 rounded-full hover:border-[#1A1A2E] transition-all duration-300 ${className}`}
        {...rest}
    >
        {children}
    </button>
);
